import { useCallback, useMemo, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import MarkdownRender from 'LuxKit/MarkdownRender'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import { pipeSeparatorMessage } from 'LuxKit/intl/separators'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import ContactUSP from '../ContactUSP'
import OfferDescription, { type OfferDescriptionHandle } from '../OfferDescription'
import OfferPageFocusAnchor from '../OfferPageFocusAnchor'
import BedbankPopularFacilities from './BedbankPopularFacilities'
import BedbankSummaryPromotions from './BedbankSummaryPromotions'
import BedbankRoomConfigurations from './BedbankRoomConfigurations'
import Image from 'components/Common/Image/Image'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import Heading from 'LuxKit/Typography/Heading'
import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import useBedbankRates from 'hooks/useBedbankRates'
import BedbankTierProgressionPanel from 'loyalty/components/Banners/BedbankTierProgressionPanel'

import { TasteOfGoldPromoCard } from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldPromoStyles'
import TasteOfGoldPromoContent from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldPromoContent'
import { BEDBANK_DESCRIPTION_SPLIT_MARKER } from 'lib/offer/bedbankDescriptionSplitMarker'
import { hasSelectedRooms } from 'lib/offer/offerPageUtil'
import OfferPageHighlightTags from '../OfferPageHighlightTags'
import { buildBedbankHighlightTagContext } from '../offerPageHighlightTagContextUtils'

const Description = styled(MarkdownRender)`
  & > h4 + p {
    margin-top: ${rem(8)};
  }
`

interface Props {
  offer: App.BedbankOffer;
  location: string;
  hasPromotions: boolean;
  checkInDate?: string;
  checkOutDate?: string;
  duration: number;
  rooms: Array<OfferPageRoom>;
  flightPrice?: number;
  flightsEnabled: boolean;
  showTasteOfGoldPromo?: boolean;
}

function BedbankPropertyDetails(props: Props) {
  const descriptionRef = useRef<OfferDescriptionHandle>(null)
  const intl = useIntl()
  const propertyLogoAlt = props.offer.property.name
    ? intl.formatMessage(
        {
          id: '4c0ZhBlII4H2cbeG37Cp',
          defaultMessage: '{propertyName} logo',
          description:
            'Alt text for a property logo shown in the bedbank offer details header when the property name is known',
        },
        { propertyName: props.offer.property.name },
      )
    : intl.formatMessage({
        id: 'uAXSwqtctssADGGcWVJh',
        defaultMessage: 'Vendor logo',
        description:
          'Fallback alt text for a property logo shown in the bedbank offer details header',
      })
  const {
    offer,
    location,
    hasPromotions,
    checkInDate,
    checkOutDate,
    duration,
    rooms,
    flightPrice,
    flightsEnabled,
    showTasteOfGoldPromo,
  } = props
  const sep = intl.formatMessage(pipeSeparatorMessage)

  const facilitiesOverride = offer.property?.facilitiesOverride

  const [{ hotelOnlyRates }, fetchingHotelOnlyRates] = useBedbankRates(
    offer.id,
    rooms,
    checkInDate,
    checkOutDate,
  )
  const isFetchingSelectedDateBedbankRates =
    !!checkInDate && !!checkOutDate && fetchingHotelOnlyRates
  const highlightTagContextBuilder = useCallback(
    () =>
      buildBedbankHighlightTagContext(
        offer,
        checkInDate && checkOutDate ? hotelOnlyRates : undefined,
      ),
    [checkInDate, checkOutDate, hotelOnlyRates, offer],
  )

  const { expandableDesc, nonExpandableDesc } = useMemo<{
    expandableDesc: string | null;
    nonExpandableDesc: string | null;
  }>(() => {
    const description = offer.description?.trim()
    if (!description) {
      return { expandableDesc: null, nonExpandableDesc: null }
    }

    const index = description.indexOf(BEDBANK_DESCRIPTION_SPLIT_MARKER)
    if (index === -1) {
      return { expandableDesc: null, nonExpandableDesc: description }
    }

    const mainDesc = description.substring(0, index).trim()
    const additionalDesc = description.slice(index).trim()

    if (mainDesc.length > 50 && additionalDesc) {
      const expandableDesc = `${mainDesc}\n\n${additionalDesc}`
      return { expandableDesc, nonExpandableDesc: null }
    } else {
      return { expandableDesc: null, nonExpandableDesc: description }
    }
  }, [offer.description])

  const descriptionPrepareSteps = useMemo(
    () => [{
      kind: 'reveal_container' as const,
      run: () => {
        descriptionRef.current?.expand()
        return { status: 'completed' as const }
      },
    }],
    [],
  )

  const roomsWithBeds = useMemo(
    () =>
      offer.packages
        .filter((p) => p.roomConfigurations.length > 0)
        .flatMap((p) => p.roomConfigurations),
    [offer.packages],
  )

  return (
    <Group direction="vertical" gap={16}>
      <OfferPageFocusAnchor targetId="page:overview" anchorRelation="parent" />
      <Group direction="vertical" gap={8}>
        <Group
          fullWidth
          direction="horizontal"
          horizontalAlign="space-between"
          verticalAlign="center"
          gap={8}
        >
          <Group direction="vertical" gap={4}>
            {(offer.property.locationTitleOverride ||
              offer.property.address) && (
              <BodyTextBlock
                data-testid="location-box"
                variant="small"
                startIcon={<LineMapMarkerIcon />}
              >
                <FormattedMessage
                  id="M2Y4p5nHLyDBKvECqnLj"
                  defaultMessage="{location} {sep} <link>View on map</link>"
                  description="Bedbank property location text with a clickable link to view the property on the map"
                  values={{
                    location,
                    sep,
                    link: (chunks) => (
                      <TextLink href="#getting-there">{chunks}</TextLink>
                    ),
                  }}
                />
              </BodyTextBlock>
            )}

            <Heading
              variant="heading2"
              as="h1"
              data-testid="bedbank-offer-name"
            >
              {offer.name}
            </Heading>

            {offer.tagline && (
              <BodyText variant="large">{offer.tagline}</BodyText>
            )}
          </Group>
          {offer.logoImageId && (
            <CSSBreakpoint min="desktop">
              <Image
                id={offer.logoImageId}
                dpr={3}
                height={75}
                enhancedFormats={false}
                alt={propertyLogoAlt}
              />
            </CSSBreakpoint>
          )}
        </Group>

        {!hasPromotions && (
          <div>
            <ProductTypeLabel productType="bedbank_hotel" />
          </div>
        )}

        {isOfferRatingDisplayable(offer.property.rating) && (
          <OfferRating
            link
            inlineLabel
            variant="small"
            rating={offer.property.rating}
          />
        )}
        {!isFetchingSelectedDateBedbankRates && (
          <OfferPageHighlightTags
            contextBuilder={highlightTagContextBuilder}
            duration={duration}
            hasSelectedDates={!!checkInDate}
            isOccupancyContextKnown={hasSelectedRooms(rooms)}
            propertyId={offer.property.id}
            rooms={rooms}
          />
        )}
        <BedbankTierProgressionPanel
          offer={offer}
          rates={hotelOnlyRates}
          nights={duration}
          checkoutDate={checkOutDate}
        />
        {showTasteOfGoldPromo && (
          <TasteOfGoldPromoCard>
            <CSSBreakpoint max="mobile">
              <TasteOfGoldPromoContent
                layout="vertical"
                size="medium"
                context="offerPage"
              />
            </CSSBreakpoint>
            <CSSBreakpoint min="tablet">
              <TasteOfGoldPromoContent
                layout="horizontal"
                size="medium"
                context="offerPage"
              />
            </CSSBreakpoint>
          </TasteOfGoldPromoCard>
        )}
      </Group>
      <Group direction="vertical" gap={24}>
        {(expandableDesc || nonExpandableDesc) && <>
          <OfferPageFocusAnchor
            targetId="page:overview:description"
            anchorRelation="next-sibling"
            focusMode="expand_then_scroll"
            prepareSteps={descriptionPrepareSteps}
          />
          {expandableDesc && <OfferDescription ref={descriptionRef} description={expandableDesc} />}
          {nonExpandableDesc && (
            <Description
              type="compact"
              fontSize="medium"
              content={nonExpandableDesc}
            />
          )}
        </>}
        {hasPromotions && offer.additionalPromoContent && (
          <MarkdownRender
            type="compact"
            fontSize="medium"
            content={offer.additionalPromoContent}
          />
        )}
        {offer.singleUnit && roomsWithBeds.length > 0 && (
          <BedbankRoomConfigurations roomsWithBeds={roomsWithBeds} />
        )}
        {!hasPromotions && !facilitiesOverride && (
          <BedbankPopularFacilities offer={offer} />
        )}
        {hasPromotions && (isLPCTacticalEnabled() || isAgentHubEnabled) && (
          <BedbankSummaryPromotions
            checkInDate={checkInDate}
            checkOutDate={checkOutDate}
            offer={offer}
            duration={duration}
            rooms={rooms}
            flightPrice={flightPrice}
            flightsEnabled={flightsEnabled}
          />
        )}

        {hasPromotions && isLPCTacticalEnabled() && (
          <div>
            <ContactUSP />
          </div>
        )}
      </Group>
    </Group>
  )
}

export default BedbankPropertyDetails
